/* eslint-disable react-hooks/exhaustive-deps */
import { SearchIcon } from '@chakra-ui/icons';
import { Box, Button, Center, Divider, HStack, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { CQTreeTable, TextInput } from '@laxmimanogna/code-quick-components';
import { createColumnHelper } from '@tanstack/react-table';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppColors from '../../../constants/AppColors';
import ROUTES from '../../../constants/Routes';
import { FONT_FAMILY } from '../../../constants/Theme';
import { AuthContext } from '../../../providers/AuthProvider';
import { DashboardContext } from '../../../providers/DashboardProvider';
import { replaceRoute, toTitleCase } from '../../../utils/common.utils';
import { getCurrentUserTypeIdObject } from '../../../utils/getCurrentUserTypeDetails';
import { defaultTreeTableProp, paginationProps } from './table_config/cq_tree_table_props';
import { priceConverter } from '../../../utils/priceConverter';
import InfoTooltip from '../../../components/InfoTooltip';
import { BatchStatusIndicatorV2 } from '../../my_charts/components/tables/table_config';
import { ClientType } from '../../../utils/clientTypeUtils';

// ─── Sub-header themes ────────────────────────────────────────────────────────
const DEPT_SUBHEADER     = { bg: '#F1F5F9', color: '#64748B', };
const PROVIDER_SUBHEADER = { bg: '#F1F5F9', color: '#64748B', };

const columnHelper = createColumnHelper();

// ─── Shared action button ─────────────────────────────────────────────────────
function ViewButton({ onClick }) {
  return (
    <Button
      size="sm"
      bg={AppColors.secondary}
      color="white"
      borderRadius="3xl"
      px={4}
      fontWeight="normal"
      fontSize="sm"
      _hover={{
        transition: 'all .1s ease',
        bgColor: AppColors.white,
        color: AppColors.secondary,
        outline: `2px solid ${AppColors.secondary}`,
      }}
      onClick={onClick}
    >
      View
    </Button>
  );
}

// ─── N/A helper ──────────────────────────────────────────────────────────────
const NA = () => <Text color={AppColors.gray} textAlign="center">N/A</Text>;

const HospitalsTableV2 = () => {
  const tableRef = useRef(null);
  const dashboardContext = useContext(DashboardContext);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const { 
    treeData, 
    isHierarchyLoading, 
    getHierarchyTree, 
    hospitalFilter, 
    setHospitalFilter,
    getHierarchyTreeWithMetrics,
    totalBatchMetadata
   } = dashboardContext;

  const {
    loggedInUserType
  } = authContext

  const isPhysiciansGroup = useMemo(() => loggedInUserType === ClientType.PHYSICIANS_GROUP, [loggedInUserType])

  const [searchText, setSearchText] = useState('');

  // ── Load when user context or filter changes ──────────────────────────────
  useEffect(() => {
    const obj = getCurrentUserTypeIdObject(authContext.currentUser);
    setHospitalFilter(prev => ({ ...prev, ...obj }));
  }, [authContext.currentUser]);


  const onFilterChange = e => setSearchText(e ? e.target.value : '');
  const onSearchAction = () => setHospitalFilter(prev => ({ ...prev, search: searchText }));

  // ─── Columns ─────────────────────────────────────────────────────────────
  // All three depths share the same column definitions.
  // Cells check info.row.depth to decide what to render.
  // Depth 0 = hospital, depth 1 = department, depth 2 = provider
  const columns = useMemo(() => !isPhysiciansGroup ? [

    // Col 1: Name
    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Hospital',
      subHeader: {
        1: { label: 'Department', ...DEPT_SUBHEADER,     sortable: true },
        2: { label: 'Provider',   ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const depth = info.row.depth;
        const val   = info.getValue();
        if (depth === 2) {
          // Provider: first_name + last_name
          const { first_name, last_name } = info.row.original;
          return <Text fontWeight={500} textAlign={"start"} fontSize="13px" color="blue.700">{val || toTitleCase(`${first_name ?? ''} ${last_name ?? ''}`.trim() )}</Text>;
        }
        if (depth === 1) return <Text textAlign={"start"} fontWeight={500} fontSize="13px" color="green.700">{toTitleCase(val)}</Text>;
        return <Text fontWeight={600} textAlign={"start"} fontSize="14px" color={AppColors.secondary}>{val}</Text>;
      },
    }),

    columnHelper.accessor(row => row, {
      id: 'state',
      header: () => 'State',
      sortValue: row => row.state ?? null,
      cell: info => {
        const row   = info.getValue();
        const depth = info.row.depth;
        if (depth === 0) {
          return <Text textAlign="center" fontSize="13px">{row.state}</Text>;
        }
      },
    }),

    // Col 2: Active Audits (hospital/dept) / Total Uploads (provider)
    columnHelper.accessor(row => row, {
      id: 'batch_status',
      header: () => 'Batch Status',
      enableSorting: false,
      subHeader: {
        1: { label: 'Batch Status',  ...DEPT_SUBHEADER,     sortable: false },
        2: { label: 'Batch Status',  ...PROVIDER_SUBHEADER, sortable: false },
      },
      sortValue: row => row.active_audits ?? row.total_uploads ?? null,
      cell: info => {
        const row   = info.getValue();
        return (
            <BatchStatusIndicatorV2 
            countDictionary={row.batch_metadata}
            />
          );
      },
    }),

    // Col 3: Chart Accuracy
    columnHelper.accessor(row => row.avg_score ?? undefined, {
      id: 'chart_accuracy',
      header: () => 'Chart Accuracy',
      enableSorting: true,
      sortUndefined: 'last',
      // sortValue: 
      subHeader: {
        1: { label: 'Chart Accuracy', ...DEPT_SUBHEADER,     sortable: true },
        2: { label: 'Chart Accuracy', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{val}%</Text> : <NA />;
      },
    }),

    // Col 4: RI Rev. Opp
    columnHelper.accessor(row => row?.total_rvu ?? undefined, {
      id: 'cq_rev_opp',
      header: () => 'RI Rev. Opp',
      sortUndefined: 'last',
      subHeader: {
        1: { label: 'RI Rev. Opp', ...DEPT_SUBHEADER,     sortable: true },
        2: { label: 'RI Rev. Opp', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{priceConverter(val)}</Text> : <NA />;
      },
    }),

    // Col 5: Action
    columnHelper.accessor(row => row.id, {
      id: 'action',
      header: () => 'Action',
      enableSorting: false,
      subHeader: {
        1: { label: 'Action', ...DEPT_SUBHEADER,     sortable: false },
        2: { label: 'Action', ...PROVIDER_SUBHEADER, sortable: false },
      },
      cell: info => {
        const row   = info.row.original;
        const depth = info.row.depth;

        if (depth === 0) {
          if (!info.row.index) {
            return;
          }
          return <ViewButton onClick={() => navigate(replaceRoute(ROUTES.HOSPITAL_DASHBOARD, { hospitalId: row.id }))} />;
        }
        if (depth === 1) {
          return <ViewButton onClick={() => navigate(replaceRoute(ROUTES.DEPARTMENT_DASHBOARD, 
            {hospitalId: info.row.getParentRow().original.id, 
            departmentId: row.id }), 
            { state: row.name })} />;
        }
        // depth === 2: provider
        return (
          <ViewButton onClick={() => navigate(replaceRoute(ROUTES.PHYSICIAN_DASHBOARD, {
            hospitalId: info.row.getParentRow().getParentRow().original.id,
            departmentId: info.row.getParentRow().original.id,
            physicianId:  row.id,
          }))} />
        );
      },
    }),

  ] : [
    // Col 1: Name
    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Specialties',
      subHeader: {
        1: { label: 'Provider',   ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const depth = info.row.depth;
        const val   = info.getValue();
        if (depth === 1) {
          // Provider: first_name + last_name
          const { first_name, last_name } = info.row.original;
          return <Text fontWeight={500} textAlign={"start"} fontSize="13px" color="blue.700">{val || toTitleCase(`${first_name ?? ''} ${last_name ?? ''}`.trim() )}</Text>;
        }
        // if (depth === 1) return <Text textAlign={"start"} fontWeight={500} fontSize="13px" color="green.700">{toTitleCase(val)}</Text>;
        return <Text fontWeight={600} textAlign={"start"} fontSize="14px" color={AppColors.secondary}>{val}</Text>;
      },
    }),

    // Col 2: Active Audits (hospital/dept) / Total Uploads (provider)
    columnHelper.accessor(row => row, {
      id: 'batch_status',
      header: () => 'Batch Status',
      enableSorting: false,
      subHeader: {
        1: { label: 'Batch Status',  ...PROVIDER_SUBHEADER, sortable: false },
      },
      sortValue: row => row.active_audits ?? row.total_uploads ?? null,
      cell: info => {
        const row   = info.getValue();
        return (
            <BatchStatusIndicatorV2 
            countDictionary={row.batch_metadata}
            />
          );
      },
    }),

    // Col 3: Chart Accuracy
    columnHelper.accessor(row => row.avg_score ?? undefined, {
      id: 'chart_accuracy',
      header: () => 'Chart Accuracy',
      enableSorting: true,
      sortUndefined: 'last',
      subHeader: {
        1: { label: 'Chart Accuracy', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{val}%</Text> : <NA />;
      },
    }),

    // Col 4: RI Rev. Opp
    columnHelper.accessor(row => row?.total_rvu ?? undefined, {
      id: 'cq_rev_opp',
      header: () => 'RI Rev. Opp',
      sortUndefined: 'last',
      subHeader: {
        1: { label: 'RI Rev. Opp', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{priceConverter(val)}</Text> : <NA />;
      },
    }),

    // Col 5: Action
    columnHelper.accessor(row => row.id, {
      id: 'action',
      header: () => 'Action',
      enableSorting: false,
      subHeader: {
        1: { label: 'Action', ...DEPT_SUBHEADER,     sortable: false },
        2: { label: 'Action', ...PROVIDER_SUBHEADER, sortable: false },
      },
      cell: info => {
        const row   = info.row.original;
        const depth = info.row.depth;
        if (depth === 0) {
          return <ViewButton onClick={() => navigate(replaceRoute(ROUTES.DEPARTMENT_DASHBOARD, 
            {hospitalId: authContext?.currentUser?.hospital_id?.id, 
            departmentId: row?.dept_id }), 
            { state: row.name })} />;
        }
        // depth === 1: provider
        return (
          <ViewButton onClick={() => navigate(replaceRoute(ROUTES.PHYSICIAN_DASHBOARD, {
            hospitalId: authContext?.currentUser?.hospital_id?.id,
            departmentId: info.row.getParentRow().original?.dept_id,
            physicianId:  row.id,
          }))} />
        );
      },
    }),
  ], [hospitalFilter, navigate, isPhysiciansGroup]);

  return (
    <Box marginTop={3} width="100%" bg={AppColors.white} padding={4} borderRadius="lg" shadow="md">
      <HStack width="100%" justifyContent="space-between" mb={3}>
        <HStack spacing={3}>
          {!isPhysiciansGroup ? (
            <>
            <Text fontSize="18px" color={AppColors.primary} fontFamily={FONT_FAMILY.ptSans}>
            Locations
          </Text>
          <InfoTooltip infoContent={
            <p><b>Locations</b> — expand each hospital to see its departments and providers. Click View to access further details, providing a comprehensive overview of each facility's performance and metrics. Non-Faciity are for those services which were not provided at any hospital</p>
          } />
          </>
          ) : (
          <>
          <Text fontSize="18px" color={AppColors.primary} fontFamily={FONT_FAMILY.ptSans}>
            Specialties
          </Text>
          <InfoTooltip infoContent={
            <p><b>Specialties</b> — expand each Specialty to see its providers. Click View to access further details, providing a comprehensive overview of each specialty/provider's performance and metrics.</p>
          } />
          </>)}
          
          {isHierarchyLoading ? <Spinner /> :(
            <>
          <BatchStatusIndicatorV2 
          countDictionary={totalBatchMetadata?.status_details ?? {}}
          />
          <Tooltip
            label="Average Chart Accuracy"
            placement="top"
            hasArrow
          >
            <Box>
            {totalBatchMetadata?.avg_score != null ? <Text fontWeight={600} textAlign={"start"} fontSize="15px" color={AppColors.secondary}>{totalBatchMetadata?.avg_score}%</Text> : <NA />}
            </Box>
          </Tooltip>
          <Tooltip
            label="Total RVU"
            placement="top"
            hasArrow
          >
            <Box>
            {totalBatchMetadata?.total_rvu != null ? <Text fontWeight={600} textAlign={"start"} fontSize="15px" color={AppColors.secondary}>${totalBatchMetadata?.total_rvu}</Text> : <NA />}
            </Box>
          </Tooltip>
          </>
          )}
        </HStack>
        <Box w="sm">
          <TextInput
            borderRadius="md"
            value={searchText}
            onChange={e => onFilterChange(e)}
            onKeyDown={e => { if (e.keyCode === 13) onSearchAction(); }}
            rightIconProps={{ zIndex: 0 }}
            placeholder="Search Hospitals"
            fontSize="md"
            rightIcon={<SearchIcon onClick={onSearchAction} color={AppColors.gray} />}
          />
        </Box>
      </HStack>
      <Divider />
      <Box
        id="fixedTableHeader"
        maxHeight="400px"
        overflow="auto"
        className="tableWraper"
        sx={{
          '& > div': { overflow: 'visible' },   // neutralize the inner overflowX Box
          'thead th': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bg: 'white',
          },
        }}
      >
      <CQTreeTable
        ref={tableRef}
        data={treeData ?? []}
        columns={columns}
        getSubRows={row => row.subRows}
        isLoading={isHierarchyLoading}
        size={9999}
        manualSortBy={false}
        {...defaultTreeTableProp}
        manualPagination={false}
        // onAsc={h  => setOrdering(h)}
        // onDesc={h => setOrdering(`-${h}`)}
        searchText={searchText}
      />
      </Box>

      {!isHierarchyLoading && !treeData?.length && (
        <Center mt={10} fontFamily={FONT_FAMILY.ptSans}>No data available</Center>
      )}
    </Box>
  );
};

export default HospitalsTableV2;